import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react'; // Back icon
import { resetPassword } from '@aws-amplify/auth'; 

// Utility to combine Tailwind classes
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

// Glassmorphism input styles - UPDATED GLOBALLY
const glassInput = 'w-full h-14 p-[12px] px-[23px] rounded-lg border border-[#228B22] bg-[#F8F7F7] text-gray-900 placeholder-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A7F3D0]';
// Button styles (retained)
const buttonBase = 'flex justify-center items-center gap-[10px] self-stretch rounded-lg h-14 px-[10px] font-semibold transition-colors duration-200 bg-[#1C731C] text-white hover:bg-[#155A15]';

// This component handles the initial step of the forgot password flow:
// collecting the identifier (now explicitly phone number) and sending the verification code.
const ForgotPasswordPhoneNumber = ({ onSendOTP, onBack, initialIdentifier = '' }) => {
    const [identifier, setIdentifier] = useState(initialIdentifier); // Changed from email/username to phone number
    const [error, setError] = useState(''); // State for displaying errors
    const [loading, setLoading] = useState(false); // State for loading indicator

    // Populate identifier if passed from Login component
    useEffect(() => {
        if (initialIdentifier) {
            setIdentifier(initialIdentifier);
        }
    }, [initialIdentifier]);

    const handleIdentifierChange = (e) => {
        let value = e.target.value;
        // Ensure it starts with '+' and only allows digits after that
        if (!value.startsWith('+')) {
            // If user types numbers first, prepend +91
            if (/^\d+$/.test(value)) {
                value = '+91' + value;
            } else {
                // If it's not starting with + and not just digits, clear or guide
                value = ''; // Or provide specific error
            }
        }
        setIdentifier(value);
    };

    // Handles the submission to send the OTP
    const handleSendOTP = async (e) => {
        e.preventDefault(); // Prevent default form submission
        setError(''); // Clear any previous errors
        setLoading(true); // Show loading indicator

        // More robust E.164 validation
        const e164Pattern = /^\+[1-9]\d{1,14}$/; 
        if (!e164Pattern.test(identifier)) {
            setError('Please enter a valid phone number in E.164 format (e.g., +919876543210).');
            setLoading(false);
            return;
        }

        try {
            // AWS Amplify Cognito Forgot Password Init Call: Use resetPassword to send the code
            await resetPassword({ username: identifier }); // Updated call to use an object
            console.log(`OTP sent to ${identifier} via Cognito.`);
            onSendOTP(identifier); // Trigger callback to navigate to OTP verification page, passing identifier
        } catch (err) {
            console.error('Error sending OTP:', err);
            // Handle specific Cognito error codes for user feedback
            if (err.code === 'UserNotFoundException') {
                setError('User not found. Please check the phone number.'); // Updated error message
            } else if (err.code === 'LimitExceededException') {
                setError('Attempt limit exceeded, please try after some time.');
            }
            else {
                setError(err.message || 'An unexpected error occurred while sending OTP. Ensure phone number sign-in is configured in Cognito.');
            }
        } finally {
            setLoading(false); // Hide loading indicator
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full"
        >
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center font-figtree leading-[1.2]">Forgot Password</h2>
            <p className="text-gray-900 text-lg text-center mb-4 font-open-sans leading-[1.5]">
                Enter your registered phone number to receive a verification code.
            </p>
            <form onSubmit={handleSendOTP} className="space-y-4">
                <div>
                    <label htmlFor="identifier" className="block text-gray-800 text-lg font-medium mb-2 leading-[1.5]">
                        Phone Number
                    </label>
                    <input
                        type="tel" // Changed input type to 'tel' for phone numbers
                        id="identifier"
                        className={glassInput}
                        placeholder="e.g., +919876543210" // Updated placeholder for E.164
                        value={identifier}
                        onChange={handleIdentifierChange} // Use custom handler for phone number input
                        required // HTML5 required validation
                    />
                </div>
                {error && <p className="text-red-600 text-sm text-center">{error}</p>} {/* Display error message */}
                <button
                    type="submit"
                    className={combineClasses(
                        buttonBase, // Base button styles
                        "w-full", // Full width
                        "transition-all duration-300 transform hover:scale-105", // Hover effects
                        loading ? "opacity-70 cursor-not-allowed" : "" // Loading state styles
                    )}
                    disabled={loading} // Disable button when loading
                >
                    {loading ? 'Sending Code...' : 'Send Verification Code'} {/* Button text changes based on loading state */}
                </button>
            </form>
            <div className="mt-4 text-center">
                <button
                    onClick={onBack} // Navigate back to the login screen
                    className="inline-block align-baseline text-lg font-poppins font-normal leading-[1.2] text-[#013220] hover:text-[#2E6B24] transition-colors"
                >
                    <ArrowLeft className="w-5 h-5 mr-2 inline-block" /> Back to Login
                </button>
            </div>
        </motion.div>
    );
};

export default ForgotPasswordPhoneNumber;
